import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import {
    alphaNumericSort,
    Measure,
    ProviderType,
    Question,
    Standard
} from "./models";
import {
    Dropdown,
    DropdownChangeEvent
} from "primereact/dropdown";
import { Button } from "primereact/button";
import CommentsSidebar from "./CommentsSidebar";
import { useLocation } from "react-router";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import httpService from "../../../services/httpService";
import { ApiEndpoints } from "../../../constants/appConstants";
import JustificationDialog from "../../Common/JustificationDialog";
import { useAuthContext } from "../../../Context/AuthContext";
import QuestionInfoComponent from "../../ProviderProfile/QuestionInfoComponent";
import JustificationSideBar from "../../Common/JustificationSideBar/JustificationSideBarComponent";
import { useAppContext } from "../../../Context";
import { OverlayPanel } from "primereact/overlaypanel";
import DisputeQuestionAdditionalInfo from "../../Common/DisputeQuestionAdditionalInfo/DisputeQuestionAdditionalInfo";
import SafeHtml from "../../Common/SafeHtml";

export default function StandardsDetailsComponent({
    standard,
    isEnabled,
    providerAgencyName,
    incentiveAmountCredentialing,
    onReviewChange,
    downLoadProviderDataByDataSource,
    updatePerformanceAreasForComments,
    evaluationStatuses,
    canDisableDropdown,
    CanShowDisputeIcon
}: {
    standard: Standard;
    isEnabled: boolean;
    onReviewChange: (data: any) => void;
    downLoadProviderDataByDataSource: (dataSource: string) => void;
    updatePerformanceAreasForComments: (measureId: number) => void;
    providerAgencyName: string;
    evaluationStatuses: any[];
    incentiveAmountCredentialing: number;
    canDisableDropdown?: boolean;
    CanShowDisputeIcon?: boolean;
}) {
    const {
        isAdministrator,
        isFinalReviewer
    } = useAuthContext();

    const [innerExpandedRows, setInnerExpandedRows] = useState<
        Record<string, any>
    >({});

    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [expandedRows, setExpandedRows] = useState<any>(null);
    const [checkedMeasures, setCheckedMeasures] = useState<any>({});
    const [sidebarVisible, setSidebarVisible] = useState(false);

    const location = useLocation();

    const [commentMeasureNumber, setCommentMeasureNumber] =
        useState("");

    const [commentMeasureId, setCommentMeasureId] =
        useState(0);

    const [commentProviderMPI, setCommentProviderMPI] =
        useState(
            location?.state?.provider?.mpi || ""
        );

    const [commentLevel, setCommentLevel] =
        useState("");

    const [
        currentReviewedMeasure,
        setCurrentReviewedMeasure
    ] = useState<Measure | null>(null);

    const [reasonDialogVisible, setReasonDialogVisible] =
        useState(false);

    const [
        reasonPopupDialogVisible,
        setReasonPopupDialogVisible
    ] = useState(false);

    const [isFailedReasonReadOnly] =
        useState(false);

    const [defaultReasons, setDefaultReasons] =
        useState<any[]>([]);

    const [selectedReasons, setSelectedReasons] =
        useState<any[]>([]);

    const [userReason, setUserReason] =
        useState("");

    const [loadingReasons] =
        useState(false);

    const { reviewYear } =
        useAppContext();

    const [isActiveReview, setIsActiveReview] =
        useState(false);

    const [
        isActiveDisputeReview,
        setIsActiveDisputeReview
    ] = useState(false);

    const [
        JustificationReasons,
        setJustificationReasons
    ] = useState<any[]>();

    const overlay =
        useRef<OverlayPanel>(null);

    const [
        currentQuestion,
        setcurrentQuestion
    ] = useState<Question>();

    const [scoringHtml, setScoringHtml] =
        useState<string>("");

    const [
        scoringLoading,
        setScoringLoading
    ] = useState(false);

    const setInnerExpandedRowsHelper = (
        measureName: string,
        newData: any,
        isSelect: boolean
    ) => {
        if (
            innerExpandedRows[measureName] &&
            !isSelect
        ) {
            setInnerExpandedRows({
                ...innerExpandedRows,
                [measureName]: undefined
            });
        } else {
            setInnerExpandedRows({
                ...innerExpandedRows,
                [measureName]: newData
            });
        }
    };

    const questionTemplateWithCalculatedFields = (
        measure: Measure
    ) => {
        const dataFields =
            measure?.questions?.filter(
                (q) => !q.isCalculatedField
            );

        const formatter =
            new Intl.NumberFormat(
                "en-US",
                {
                    style: "currency",
                    currency: "USD"
                }
            );

        return (
            <>
                {dataFields
                    ?.filter(
                        (q: Question) =>
                            !q.displayConfig ||
                            !q.displayConfig
                                ?.canHide
                    )
                    .filter(
                        (q) =>
                            q.questionTitle
                    )
                    .map(
                        (
                            question: Question
                        ) => (
                            <div
                                key={
                                    question.questionTitle
                                }
                            >
                                <div>
                                    <h4>
                                        Question{" "}
                                        {
                                            question.questionTitle
                                        }

                                        <button
                                            className="pi pi-info-circle scoring-icon"
                                            onClick={(
                                                e
                                            ) => {
                                                e.stopPropagation();

                                                getScoringReasonForMeasure(
                                                    question,
                                                    e
                                                );
                                            }}
                                        ></button>
                                    </h4>
                                </div>

                                <p
                                    style={{
                                        whiteSpace:
                                            "pre-line"
                                    }}
                                >
                                    <SafeHtml
                                        html={
                                            question.questionDescription
                                        }
                                    />
                                </p>

                                <QuestionInfoComponent
                                    downLoadProviderDataByDataSource={
                                        downLoadProviderDataByDataSource
                                    }
                                    question={
                                        question
                                    }
                                />

                                {isActiveDisputeReview &&
                                    (question.comment ||
                                        question.attachments) && (
                                        <DisputeQuestionAdditionalInfo
                                            question={
                                                question
                                            }
                                        />
                                    )}

                                <Divider />
                            </div>
                        )
                    )}

                <div>
                    <h4>
                        Initiative Payment
                        Amount:{" "}
                        <InputText
                            disabled
                            value={String(
                                formatter.format(
                                    incentiveAmountCredentialing
                                )
                            )}
                        />
                    </h4>
                </div>
            </>
        );
    };

    const getScoringReasonForMeasure =
        async (
            question: Question,
            e: any
        ) => {
            try {
                setcurrentQuestion(question);
                setScoringLoading(true);
                setScoringHtml("");

                if (overlay.current) {
                    overlay.current.toggle(e);
                }

                const response: any =
                    await httpService.get(
                        `${ApiEndpoints.GetScoringCriteria}/${question.questionId}`
                    );

                setScoringHtml(
                    response?.data
                        ?.htmlScoringCriteria ||
                        ""
                );
            } catch (error: any) {
                console.error(
                    "[StandardsDetailsComponent] Error loading scoring guidance.",
                    error
                );

                console.error(
                    "[StandardsDetailsComponent] Error loading scoring guidance details.",
                    {
                        message:
                            error?.message,
                        responseStatus:
                            error?.response
                                ?.status,
                        innerExceptionMessage:
                            error?.cause
                                ?.message,
                        stack:
                            error?.stack
                    }
                );

                setScoringHtml(
                    "<div style='color:red;'>Failed to load scoring guidance.</div>"
                );
            } finally {
                setScoringLoading(false);
            }
        };

    const getSelectedReasons =
        async (
            event: any,
            data: Measure
        ) => {
            try {
                const mpi =
                    commentProviderMPI;

                const response: any =
                    await httpService.get(
                        `${ApiEndpoints.getFailCommentsForMeasure}/${mpi}/${data.measureId}/${reviewYear}`
                    );

                setCurrentReviewedMeasure(
                    data
                );

                const reasons =
                    response?.data?.value
                        ?.reasons;

                if (
                    reasons &&
                    Array.isArray(
                        reasons
                    ) &&
                    reasons.length > 0
                ) {
                    const mappedReasons =
                        reasons
                            .filter(
                                (
                                    r: any
                                ) =>
                                    r &&
                                    r.reasonId !==
                                        undefined &&
                                    r.reasonText !==
                                        undefined
                            )
                            .map(
                                (
                                    r: any
                                ) => ({
                                    reasonId:
                                        r.reasonId,
                                    reasonText:
                                        r.reasonText
                                })
                            );

                    setDefaultReasons(
                        mappedReasons
                    );

                    setSelectedReasons(
                        mappedReasons.map(
                            (
                                r: any
                            ) =>
                                r.reasonId
                        )
                    );

                    setUserReason(
                        response?.data
                            ?.value
                            ?.userReason ||
                            response?.data
                                ?.value
                                ?.additionalComment ||
                            ""
                    );

                    setReasonPopupDialogVisible(
                        true
                    );
                }
            } catch (error: any) {
                console.error(
                    "[StandardsDetailsComponent] Error fetching selected reasons.",
                    error
                );

                console.error(
                    "[StandardsDetailsComponent] Error fetching selected reasons details.",
                    {
                        message:
                            error?.message,
                        responseStatus:
                            error?.response
                                ?.status,
                        innerExceptionMessage:
                            error?.cause
                                ?.message,
                        stack:
                            error?.stack
                    }
                );
            }

            event.stopPropagation();
        };

    const numberOfQuestionsTemplate = (
        measure: Measure
    ) => {
        const filteredQuestions =
            measure.questions?.filter(
                (q) =>
                    !q.isCalculatedField &&
                    (!q.displayConfig ||
                        !q.displayConfig
                            ?.canHide)
            ) || [];

        return filteredQuestions.length;
    };

    const onReasonChange = (
        e: any
    ) => {
        if (
            isFailedReasonReadOnly
        ) {
            return;
        }

        const checked =
            e.checked;

        const value =
            e.value ??
            e.target?.value;

        setSelectedReasons(
            (prev: any[]) =>
                checked
                    ? [
                          ...prev,
                          value
                      ]
                    : prev.filter(
                          (id) =>
                              id !==
                              value
                      )
        );
    };

    const isActiveReviewCycle =
        async () => {
            try {
                const response: any =
                    await httpService.get(
                        ApiEndpoints.IsPfpActiveReview
                    );

                setIsActiveReview(
                    response?.data
                );

                console.log(
                    "[StandardsDetailsComponent] Is active review cycle:",
                    response?.data
                );
            } catch (error: any) {
                console.error(
                    "[StandardsDetailsComponent] Error loading active review cycle.",
                    error
                );

                console.error(
                    "[StandardsDetailsComponent] Error loading active review cycle details.",
                    {
                        message:
                            error?.message,
                        responseStatus:
                            error?.response
                                ?.status,
                        innerExceptionMessage:
                            error?.cause
                                ?.message,
                        stack:
                            error?.stack
                    }
                );
            }
        };

    const isActiveDisputeReviewCycle =
        async () => {
            try {
                const response: any =
                    await httpService.get(
                        ApiEndpoints.IsPfpActiveDisputeCycle
                    );

                setIsActiveDisputeReview(
                    response?.data
                );

                console.log(
                    "[StandardsDetailsComponent] Is active dispute cycle:",
                    response?.data
                );
            } catch (error: any) {
                console.error(
                    "[StandardsDetailsComponent] Error loading active dispute cycle.",
                    error
                );

                console.error(
                    "[StandardsDetailsComponent] Error loading active dispute cycle details.",
                    {
                        message:
                            error?.message,
                        responseStatus:
                            error?.response
                                ?.status,
                        innerExceptionMessage:
                            error?.cause
                                ?.message,
                        stack:
                            error?.stack
                    }
                );
            }
        };

    function saveNotApprovedResponse(): void {
        try {
            if (
                !currentReviewedMeasure
            ) {
                return;
            }

            const reasons =
                JustificationReasons?.filter(
                    (r) =>
                        selectedReasons.includes(
                            r.reasonId
                        )
                ) || [];

            onReviewChange({
                reviewResponseId:
                    currentReviewedMeasure.reviewResponseId,
                measureId:
                    currentReviewedMeasure.measureId,
                reviewResponse:
                    "na",
                reasons,
                userReason
            });

            setReasonDialogVisible(
                false
            );

            setSelectedReasons(
                []
            );

            setUserReason("");

            setCurrentReviewedMeasure(
                null
            );
        } catch (error: any) {
            console.error(
                "[StandardsDetailsComponent] Error saving not approved response.",
                error
            );

            console.error(
                "[StandardsDetailsComponent] Error saving not approved response details.",
                {
                    message:
                        error?.message,
                    innerExceptionMessage:
                        error?.cause
                            ?.message,
                    stack:
                        error?.stack
                }
            );
        }
    }

    const handleReviewChange = (
        data: Measure,
        status: string
    ) => {
        try {
            const currentStatusCode =
                (
                    data?.reviewResponse ||
                    ""
                )
                    .toString()
                    .toLowerCase()
                    .trim();

            const newStatusCode =
                (status || "")
                    .toString()
                    .toLowerCase()
                    .trim();

            if (
                currentStatusCode ===
                newStatusCode
            ) {
                return;
            }

            if (
                status === "na"
            ) {
                setCurrentReviewedMeasure(
                    data
                );

                setSelectedReasons(
                    []
                );

                setUserReason("");

                setReasonDialogVisible(
                    true
                );

                return;
            }

            onReviewChange({
                reviewResponseId:
                    data.reviewResponseId,
                measureId:
                    data.measureId,
                reviewResponse:
                    status
            });
        } catch (error: any) {
            console.error(
                "[StandardsDetailsComponent] Error handling review change.",
                error
            );

            console.error(
                "[StandardsDetailsComponent] Error handling review change details.",
                {
                    message:
                        error?.message,
                    innerExceptionMessage:
                        error?.cause
                            ?.message,
                    stack:
                        error?.stack
                }
            );
        }
    };

    const getReasons =
        async () => {
            try {
                const response: any =
                    await httpService.get(
                        `${ApiEndpoints.GetReasons}/${reviewYear}/${ProviderType.SCO}/${currentReviewedMeasure?.measureId}/${standard.tierId}/${standard.standardId}`
                    );

                console.log(
                    "[StandardsDetailsComponent] Reasons response:",
                    response
                );

                if (
                    response.status ===
                    200
                ) {
                    setJustificationReasons(
                        response.data.value
                    );
                }
            } catch (error: any) {
                console.error(
                    "[StandardsDetailsComponent] Error loading reasons.",
                    error
                );

                console.error(
                    "[StandardsDetailsComponent] Error loading reasons details.",
                    {
                        message:
                            error?.message,
                        responseStatus:
                            error?.response
                                ?.status,
                        innerExceptionMessage:
                            error?.cause
                                ?.message,
                        stack:
                            error?.stack
                    }
                );
            }
        };

    const approvedCheckbox = (
        data: Measure
    ) => {
        const status = [
            {
                code: "null",
                name: "Not reviewed"
            },
            {
                code: "a",
                name: "Pass"
            },
            {
                code: "na",
                name: "Fail"
            }
        ];

        const currentCode =
            String(
                data.reviewResponse?.toLowerCase?.() ||
                    "null"
            );

        const filteredStatus =
            isFinalReviewer()
                ? status.filter(
                      (s) =>
                          s.name !==
                          "Not reviewed"
                  )
                : status;

        return (
            <>
                <Dropdown
                    disabled={
                        isAdministrator() ||
                        !isEnabled ||
                        !isActiveReview ||
                        evaluationStatuses.find(
                            (
                                option
                            ) =>
                                option.code ===
                                currentCode
                        )?.isDisable ||
                        (canDisableDropdown ??
                            false)
                    }
                    value={status.find(
                        (
                            option
                        ) =>
                            option.code ===
                            currentCode
                    )}
                    onChange={(
                        e: DropdownChangeEvent
                    ) => {
                        const code =
                            e.value.code;

                        handleReviewChange(
                            data,
                            code
                        );

                        setCheckedMeasures({
                            ...checkedMeasures,
                            [data.measureNumber]:
                                code
                        });
                    }}
                    onClick={(e) =>
                        e.stopPropagation()
                    }
                    onMouseDown={(e) =>
                        e.stopPropagation()
                    }
                    options={
                        filteredStatus
                    }
                    optionLabel="name"
                    appendTo={
                        document.body
                    }
                    placeholder="Select Approval Status"
                />

                {String(
                    data.reviewResponse?.toLowerCase?.() ||
                        null
                ) === "na" && (
                    <>
                        <i
                            className="pi pi-comments measure-not-approved-icon"
                            style={{
                                marginLeft: 8,
                                cursor:
                                    "pointer",
                                fontSize:
                                    "1.2rem"
                            }}
                            onClick={(
                                e
                            ) =>
                                getSelectedReasons(
                                    e,
                                    data
                                )
                            }
                            title="View Failed Reasons"
                        />

                        <Tooltip target=".pi-comments" />
                    </>
                )}
            </>
        );
    };

    const addCommentButton = (
        measure: Measure
    ) => {
        const openCommentsSidebar =
            (e?: any) => {
                if (
                    e?.stopPropagation
                ) {
                    e.stopPropagation();
                }

                setCommentMeasureId(
                    measure?.measureId
                );

                setCommentMeasureNumber(
                    measure?.measureNumber
                );

                setCommentLevel(
                    "measure"
                );

                setSidebarVisible(
                    true
                );
            };

        return (
            <div className="d-flex justify-content-space-between">
                <Button
                    disabled={
                        !isEnabled ||
                        (canDisableDropdown ??
                            false)
                    }
                    onClick={
                        openCommentsSidebar
                    }
                >
                    Add comment
                </Button>

                {measure?.isCommentsAvailable ? (
                    <Button
                        severity="secondary"
                        icon="pi pi-comments"
                        outlined
                        text
                        onClick={
                            openCommentsSidebar
                        }
                        disabled={
                            canDisableDropdown ??
                            false
                        }
                    />
                ) : (
                    <div>
                        <i
                            className="pi"
                            style={{
                                minWidth:
                                    "34px"
                            }}
                        ></i>
                    </div>
                )}
            </div>
        );
    };

    const columnDisputeQuesTemplate =
        () => {
            return CanShowDisputeIcon ? (
                <span
                    style={{
                        marginLeft: 8
                    }}
                >
                    <i
                        className="pi pi-exclamation-circle"
                        style={{
                            width:
                                "20px",
                            paddingTop:
                                "5px",
                            color:
                                "red",
                            fontSize:
                                "1.2rem"
                        }}
                        title="Dispute Measure"
                    ></i>

                    <Tooltip target=".pi-exclamation-circle" />
                </span>
            ) : (
                ""
            );
        };

    const openSidebar = () =>
        setReasonDialogVisible(
            true
        );

    const closeSidebar = () =>
        setReasonDialogVisible(
            false
        );

    useEffect(() => {
        setCommentProviderMPI(
            location?.state?.provider
                ?.mpi || ""
        );

        void isActiveReviewCycle();
        void isActiveDisputeReviewCycle();
    }, []);

    useEffect(() => {
        if (selectedRow) {
            const key =
                selectedRow?.measureNumber;

            let innerData: any =
                {};

            if (
                innerExpandedRows[
                    key
                ]
            ) {
                innerData =
                    innerExpandedRows[
                        key
                    ];
            }

            setExpandedRows(
                innerData
            );
        } else {
            setExpandedRows(
                null
            );
        }
    }, [
        selectedRow,
        innerExpandedRows
    ]);

    useEffect(() => {
        if (
            currentReviewedMeasure
        ) {
            void getReasons();
        }
    }, [currentReviewedMeasure]);

    return (
        <div>
            <DataTable
                selectionMode="single"
                selection={
                    selectedRow
                }
                value={[
                    ...(
                        standard?.measures ||
                        []
                    )
                ].sort((a, b) =>
                    alphaNumericSort(
                        a.measureNumber,
                        b.measureNumber
                    )
                )}
                expandedRows={
                    expandedRows
                }
                rowExpansionTemplate={
                    questionTemplateWithCalculatedFields
                }
                onRowToggle={(e) =>
                    setExpandedRows(
                        e.data
                    )
                }
                onRowUnselect={(
                    e
                ) => {
                    setSelectedRow(
                        null
                    );

                    setInnerExpandedRowsHelper(
                        e.data
                            ?.measureNumber,
                        [e.data],
                        false
                    );
                }}
                onRowSelect={(
                    e
                ) => {
                    setInnerExpandedRowsHelper(
                        e.data
                            ?.measureNumber,
                        [e.data],
                        true
                    );

                    setSelectedRow(
                        e.data
                    );
                }}
                onRowMouseEnter={(
                    e
                ) => {
                    setCommentMeasureId(
                        e.data
                            ?.measureId
                    );

                    setCommentMeasureNumber(
                        e.data
                            ?.measureNumber
                    );

                    setCommentLevel(
                        "measure"
                    );
                }}
            >
                <Column
                    expander
                    style={{
                        width: "3rem"
                    }}
                />

                <Column
                    field="numberQuestions"
                    body={
                        numberOfQuestionsTemplate
                    }
                    header="Number of Questions"
                />

                <Column
                    field="numberQuestions"
                    body={
                        approvedCheckbox
                    }
                    header="Approval Status"
                />

                <Column
                    field="Comments"
                    header={
                        columnDisputeQuesTemplate
                    }
                    body={
                        addCommentButton
                    }
                />
            </DataTable>

            {sidebarVisible && (
                <CommentsSidebar
                    {...{
                        sidebarVisible,
                        setSidebarVisible,
                        commentProviderMPI,
                        commentMeasureId,
                        commentMeasureNumber,
                        commentLevel,
                        updatePerformanceAreasForComments,
                        providerAgencyName
                    }}
                />
            )}

            {defaultReasons != null &&
                defaultReasons.length >
                    0 && (
                    <JustificationDialog
                        header="Rationale for Fail Determination"
                        message={`Please provide your reasoning below for why this SCO does not qualify for the ${commentMeasureNumber} initiative program. Select as many reasons as are applicable and provide a written explanation of your reasoning.`}
                        style={{
                            width:
                                "50vw"
                        }}
                        onHide={() =>
                            setReasonPopupDialogVisible(
                                false
                            )
                        }
                        visible={
                            reasonPopupDialogVisible
                        }
                        isReadOnly={
                            isAdministrator() ||
                            !isActiveReview ||
                            !isEnabled ||
                            (canDisableDropdown ??
                                false)
                        }
                        reasons={
                            defaultReasons
                        }
                        selectedReasonIds={
                            selectedReasons
                        }
                        userReason={
                            userReason
                        }
                        onReasonChange={
                            onReasonChange
                        }
                        onUserReasonChange={
                            setUserReason
                        }
                        onSave={() => {
                            setReasonPopupDialogVisible(
                                false
                            );

                            openSidebar();
                        }}
                        loading={
                            loadingReasons
                        }
                    />
                )}

            {reasonDialogVisible && (
                <JustificationSideBar
                    header={`Provide Justification for ${commentMeasureNumber}`}
                    message="Please select at least one reason that this initiative is Fail"
                    style={{
                        width: "50vw"
                    }}
                    onHide={() => {
                        setCurrentReviewedMeasure(
                            null
                        );

                        closeSidebar();
                    }}
                    visible={
                        reasonDialogVisible
                    }
                    isReadOnly={
                        false
                    }
                    reasons={
                        JustificationReasons ||
                        []
                    }
                    selectedReasonIds={selectedReasons.map(
                        (
                            reasonId: any
                        ) =>
                            reasonId
                    )}
                    userReason={
                        userReason
                    }
                    onReasonChange={
                        onReasonChange
                    }
                    onUserReasonChange={
                        setUserReason
                    }
                    onSave={
                        saveNotApprovedResponse
                    }
                    loading={
                        loadingReasons
                    }
                />
            )}

            <OverlayPanel
                ref={overlay}
                showCloseIcon
                closeOnEscape
            >
                <div
                    style={{
                        display:
                            "flex",
                        flexDirection:
                            "column"
                    }}
                >
                    <strong>
                        Question Scoring
                        Guidance
                    </strong>

                    <Divider />

                    <div>
                        <p
                            style={{
                                whiteSpace:
                                    "pre-line"
                            }}
                        >
                            <SafeHtml
                                html={
                                    currentQuestion?.questionDescription ??
                                    ""
                                }
                            />
                        </p>
                    </div>

                    <div>
                        <div>
                            <strong>
                                Scoring
                                Guidance:{" "}
                            </strong>
                        </div>

                        <br />

                        {scoringLoading && (
                            <span>
                                Loading...
                            </span>
                        )}

                        {!scoringLoading && (
                            <div>
                                <SafeHtml
                                    html={
                                        scoringHtml
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </OverlayPanel>
        </div>
    );
}